// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Hand-rolled SVG chart primitives. No chart library — see factory rules. */
import { escapeHtml, formatNumber } from './utils/format';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function niceCeil(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  const norm = max / mag;
  let step: number;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  return step * mag;
}

export function ticks(max: number, count = 5): number[] {
  const top = niceCeil(max);
  return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

const MARGIN = { top: 18, right: 104, bottom: 34, left: 52 };

export interface LineSeries {
  id: string;
  label: string;
  colour: string;
  points: Array<[number, number]>; // [x (year), y (value/rank)]
}

export interface LineOpts {
  width?: number;
  height?: number;
  xDomain?: [number, number];
  yDomain?: [number, number];
  invertY?: boolean; // rank charts: 1 at top
  yTickVals?: number[];
  yFmt?: (v: number) => string;
  valueNoun?: string; // for tooltips, e.g. "babies" or "rank"
  gapThreshold?: number; // break line when consecutive x differ by more than this
  onPick?: (id: string) => void;
  endLabels?: boolean;
  dotRadius?: number;
}

/** General multi-series line chart (used for name trajectories and diversity). */
export function lineChart(series: LineSeries[], opts: LineOpts = {}): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 380;
  const m = MARGIN;
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const gap = opts.gapThreshold ?? 1;
  const yFmt = opts.yFmt ?? ((v: number) => formatNumber(v));
  const noun = opts.valueNoun ?? '';

  const allX = series.flatMap((s) => s.points.map((p) => p[0]));
  const allY = series.flatMap((s) => s.points.map((p) => p[1]));
  const xDomain = opts.xDomain ?? [Math.min(...allX), Math.max(...allX)];
  const yDomain = opts.yDomain ?? [0, niceCeil(Math.max(...allY, 1))];
  const [x0, x1] = xDomain;
  const [y0, y1] = yDomain;

  const xAt = (x: number) => m.left + (x1 === x0 ? plotW / 2 : ((x - x0) / (x1 - x0)) * plotW);
  const yAt = (y: number) => {
    const t = y1 === y0 ? 0.5 : (y - y0) / (y1 - y0);
    return opts.invertY ? m.top + t * plotH : m.top + plotH - t * plotH;
  };

  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  const yTickVals = opts.yTickVals ?? ticks(y1, 4);
  for (const t of yTickVals) {
    if (t < Math.min(y0, y1) || t > Math.max(y0, y1)) continue;
    const y = yAt(t);
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = yFmt(t);
    svg.appendChild(lbl);
  }

  // x labels (years) — about a dozen
  const span = x1 - x0;
  const step = Math.max(1, Math.ceil(span / 12 / 5) * 5);
  for (let x = Math.ceil(x0 / step) * step; x <= x1; x += step) {
    const px = xAt(x);
    const lbl = el('text', { class: 'axis-label', x: px, y: height - m.bottom + 14, 'text-anchor': 'middle' });
    lbl.textContent = String(x);
    svg.appendChild(lbl);
  }
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));

  for (const s of series) {
    const pts = [...s.points].sort((a, b) => a[0] - b[0]);
    // split into contiguous segments
    let seg: Array<[number, number]> = [];
    const flush = () => {
      if (seg.length >= 2) {
        const poly = el('polyline', {
          points: seg.map(([x, y]) => `${xAt(x).toFixed(1)},${yAt(y).toFixed(1)}`).join(' '),
          fill: 'none',
          stroke: s.colour,
          'stroke-width': 2.4,
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
        });
        svg.appendChild(poly);
      } else if (seg.length === 1) {
        // lone point handled by the dot below
      }
      seg = [];
    };
    let prevX: number | null = null;
    for (const p of pts) {
      if (prevX != null && p[0] - prevX > gap) flush();
      seg.push(p);
      prevX = p[0];
    }
    flush();
    // dots + tooltips
    for (const [x, y] of pts) {
      const dot = el('circle', {
        cx: xAt(x),
        cy: yAt(y),
        r: opts.dotRadius ?? 2.6,
        fill: s.colour,
        'data-tip': `${s.label} · ${x}: ${yFmt(y)}${noun ? ` ${noun}` : ''}`,
        'aria-label': `${s.label} ${x} ${yFmt(y)}`,
        style: opts.onPick ? 'cursor:pointer' : '',
      });
      if (opts.onPick) dot.addEventListener('click', () => opts.onPick!(s.id));
      svg.appendChild(dot);
    }
    // end label
    if (opts.endLabels !== false && pts.length) {
      const last = pts[pts.length - 1];
      const lbl = el('text', {
        x: xAt(last[0]) + 8,
        y: yAt(last[1]),
        'dominant-baseline': 'middle',
        'font-size': 11,
        'font-weight': 600,
        fill: s.colour,
      });
      lbl.textContent = s.label;
      svg.appendChild(lbl);
    }
  }
  return svg;
}

// ── Bump chart (ranks over time) ──────────────────────────────────────────────
export interface BumpDatum {
  key: string;
  name: string;
  colour: string;
  ranks: Map<number, number>; // year -> rank
}
export interface BumpPlot {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Pure: pixel points per entry given the plot box, years and rank cap. Testable. */
export function bumpPoints(
  entries: BumpDatum[],
  years: number[],
  maxRank: number,
  plot: BumpPlot,
): Map<string, Array<[number, number]>> {
  const out = new Map<string, Array<[number, number]>>();
  const x0 = years[0];
  const x1 = years[years.length - 1];
  const xAt = (x: number) => plot.x + (x1 === x0 ? plot.w / 2 : ((x - x0) / (x1 - x0)) * plot.w);
  const yAt = (r: number) => plot.y + ((r - 1) / Math.max(1, maxRank - 1)) * plot.h;
  for (const e of entries) {
    const pts: Array<[number, number]> = [];
    for (const y of years) {
      const r = e.ranks.get(y);
      if (r != null && r <= maxRank) pts.push([xAt(y), yAt(r)]);
    }
    out.set(e.key, pts);
  }
  return out;
}

export interface BumpOpts {
  width?: number;
  height?: number;
  maxRank?: number;
  onPick?: (key: string) => void;
}

/** Bump chart SVG with hover-to-highlight one name's whole line. */
export function bumpChart(entries: BumpDatum[], years: number[], opts: BumpOpts = {}): SVGSVGElement {
  const width = opts.width ?? 1080;
  const height = opts.height ?? 560;
  const maxRank = opts.maxRank ?? 15;
  const m = { top: 16, right: 128, bottom: 30, left: 34 };
  const plot: BumpPlot = { x: m.left, y: m.top, w: width - m.left - m.right, h: height - m.top - m.bottom };
  const x0 = years[0];
  const x1 = years[years.length - 1];
  const xAt = (x: number) => plot.x + (x1 === x0 ? plot.w / 2 : ((x - x0) / (x1 - x0)) * plot.w);
  const yAt = (r: number) => plot.y + ((r - 1) / Math.max(1, maxRank - 1)) * plot.h;

  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  // rank gridlines (1, 5, 10, 15…)
  for (let r = 1; r <= maxRank; r += r === 1 ? 4 : 5) {
    const y = yAt(r);
    svg.appendChild(el('line', { class: 'grid-line', x1: plot.x, y1: y, x2: plot.x + plot.w, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: plot.x - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = `#${r}`;
    svg.appendChild(lbl);
  }
  const span = x1 - x0;
  const step = Math.max(1, Math.ceil(span / 10 / 5) * 5);
  for (let x = Math.ceil(x0 / step) * step; x <= x1; x += step) {
    const lbl = el('text', { class: 'axis-label', x: xAt(x), y: height - m.bottom + 14, 'text-anchor': 'middle' });
    lbl.textContent = String(x);
    svg.appendChild(lbl);
  }

  const pointMap = bumpPoints(entries, years, maxRank, plot);
  const groups: SVGGElement[] = [];

  entries.forEach((e) => {
    const pts = pointMap.get(e.key) || [];
    if (!pts.length) return;
    const g = el('g', { 'data-key': e.key, style: opts.onPick ? 'cursor:pointer' : '' });
    // contiguous segments (break where a year is missing)
    const yearsRanked = years.filter((y) => {
      const r = e.ranks.get(y);
      return r != null && r <= maxRank;
    });
    let seg: Array<[number, number]> = [];
    let prevY: number | null = null;
    const flush = () => {
      if (seg.length >= 2)
        g.appendChild(
          el('polyline', {
            points: seg.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' '),
            fill: 'none',
            stroke: e.colour,
            'stroke-width': 3,
            'stroke-linejoin': 'round',
            'stroke-linecap': 'round',
            opacity: 0.85,
          }),
        );
      seg = [];
    };
    yearsRanked.forEach((yr) => {
      if (prevY != null && yr - prevY > 1) flush();
      seg.push([xAt(yr), yAt(e.ranks.get(yr) as number)]);
      prevY = yr;
    });
    flush();
    // dots
    for (const yr of yearsRanked) {
      const r = e.ranks.get(yr) as number;
      g.appendChild(
        el('circle', {
          cx: xAt(yr),
          cy: yAt(r),
          r: 3.2,
          fill: e.colour,
          stroke: '#fffefb',
          'stroke-width': 1,
          'data-tip': `${e.name} · ${yr}: #${r}`,
        }),
      );
    }
    // end label at the last ranked year
    const lastYear = yearsRanked[yearsRanked.length - 1];
    const lastRank = e.ranks.get(lastYear) as number;
    const lbl = el('text', {
      x: xAt(lastYear) + 7,
      y: yAt(lastRank),
      'dominant-baseline': 'middle',
      'font-size': 12,
      'font-weight': 700,
      fill: e.colour,
    });
    lbl.textContent = e.name;
    g.appendChild(lbl);

    if (opts.onPick) g.addEventListener('click', () => opts.onPick!(e.key));
    // hover highlight
    g.addEventListener('mouseenter', () => {
      for (const other of groups) if (other !== g) other.style.opacity = '0.15';
      g.style.opacity = '1';
    });
    g.addEventListener('mouseleave', () => {
      for (const other of groups) other.style.opacity = '1';
    });
    groups.push(g);
    svg.appendChild(g);
  });
  return svg;
}

// ── Histogram ────────────────────────────────────────────────────────────────
export interface HistBin {
  lo: number;
  hi: number;
  count: number;
}
export function histogramChart(
  bins: HistBin[],
  opts: { width?: number; height?: number; colour?: string; xFmt?: (v: number) => string; onPick?: (i: number) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const height = opts.height ?? 260;
  const m = { top: 14, right: 16, bottom: 40, left: 46 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const maxC = niceCeil(Math.max(...bins.map((b) => b.count), 1));
  const xFmt = opts.xFmt ?? ((v: number) => formatNumber(v));
  const colour = opts.colour ?? 'var(--accent-primary)';
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  for (const t of ticks(maxC, 4)) {
    const y = m.top + plotH - (t / maxC) * plotH;
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = formatNumber(t);
    svg.appendChild(lbl);
  }
  const slot = plotW / Math.max(1, bins.length);
  const barW = Math.max(1, slot * 0.86);
  bins.forEach((b, i) => {
    const x = m.left + i * slot + (slot - barW) / 2;
    const h = Math.max(0.5, (b.count / maxC) * plotH);
    const y = m.top + plotH - h;
    const tip = escapeHtml(`${xFmt(b.lo)}–${xFmt(b.hi)}: ${formatNumber(b.count)}`);
    const bar = el('rect', { class: 'bar', x, y, width: barW, height: h, rx: 2, fill: colour, 'data-tip': tip, 'aria-label': tip });
    if (opts.onPick) {
      bar.style.cursor = 'pointer';
      bar.addEventListener('click', () => opts.onPick!(i));
    }
    svg.appendChild(bar);
    if (i % Math.max(1, Math.ceil(bins.length / 10)) === 0) {
      const lbl = el('text', { class: 'axis-label', x: x + barW / 2, y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = xFmt(b.lo);
      svg.appendChild(lbl);
    }
  });
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));
  return svg;
}

export { escapeHtml };
